import type { MultipleQueriesQuery } from '@algolia/client-search'
import { withCleared } from '@/browse/browse_state'
import type { BrowseState, BrowseStateConfig, FilterDefinition } from '@/browse/browse_state'

export interface SearchFilterDefinition extends FilterDefinition {
  attribute: string
}

// Counted to build an option list, with no filter of its own.
export type CountOnlyFacet = Pick<SearchFilterDefinition, 'key' | 'attribute'>

export interface BrowseSearchConfig extends BrowseStateConfig {
  facets: SearchFilterDefinition[]
  countOnlyFacets?: CountOnlyFacet[]
  // A function when the limit depends on who is searching, such as a non-premium preview.
  hitsPerPage: number | ((state: BrowseState) => number)
  indexName: (state: BrowseState) => string
  // The keyword as Algolia is asked for it, when that differs from the one the page shows.
  keywordForSearch?: (keyword: string) => string
  // Applied to the results, and to each count query whose plan applies it.
  algoliaFilters?: (state: BrowseState) => string
  fixedFacetFilters?: (state: BrowseState) => string[][]
  searchPlan: (state: BrowseState) => SearchPlan
  isReadyForSearch?: () => boolean
  // Set on a page that queries Algolia only once something is chosen, so Algolia's search analytics
  // do not count empty page views as searches.
  hasSearchCriteria?: (state: BrowseState) => boolean
}

export type FacetCounts = Record<string, Record<string, number>>

// One extra query that counts the options of facetKeys with the filters of excludedKeys left out.
export interface CountQueryPlan {
  facetKeys: string[]
  excludedKeys: string[]
  appliesAlgoliaFilters: boolean
}

// Facets counted under exactly the filters the results use ride on the results query; every
// other count needs a query of its own.
export interface SearchPlan {
  hitsFacetKeys: string[]
  countQueries: CountQueryPlan[]
}

interface QueryContext {
  indexName: string
  algoliaFilters: string
}

function attributeOf(config: BrowseSearchConfig, key: string): string {
  const countedFacets: CountOnlyFacet[] = [...config.facets, ...(config.countOnlyFacets ?? [])]
  const facet = countedFacets.find((candidate) => candidate.key === key)
  if (!facet) {
    throw new Error(`No facet is defined for "${key}"`)
  }
  return facet.attribute
}

function facetFilterGroup(facet: SearchFilterDefinition, selectedValues: string[]): string[] {
  return selectedValues.map((value) => `${facet.attribute}:${value}`)
}

// Algolia ANDs the groups and ORs the values inside each, so every refined facet becomes one
// group of its selected values: grades K or 1, and subject Math.
function facetFilters(config: BrowseSearchConfig, state: BrowseState): string[][] {
  const refinedFacets = config.facets.filter((facet) => state.refinements[facet.key]?.length)
  const refinementGroups = refinedFacets.map((facet) => facetFilterGroup(facet, state.refinements[facet.key]))
  const fixedGroups = config.fixedFacetFilters?.(state) ?? []
  return [...fixedGroups, ...refinementGroups]
}

function searchedKeyword(config: BrowseSearchConfig, keyword: string): string {
  return config.keywordForSearch?.(keyword) ?? keyword
}

function hitsPerPageFor(config: BrowseSearchConfig, state: BrowseState): number {
  if (typeof config.hitsPerPage === 'number') {
    return config.hitsPerPage
  }
  return config.hitsPerPage(state)
}

// Two keys can count the same attribute, such as grades and a subject's grades, but a query
// names each attribute once.
function attributesToCount(config: BrowseSearchConfig, keys: string[]): string[] {
  const attributes = keys.map((key) => attributeOf(config, key))
  return [...new Set(attributes)]
}

// A secured API key can embed hitsPerPage, which then overrides the query's; it cannot override
// offset and length, so they carry the limit.
function firstHits(limit: number) {
  return { offset: 0, length: limit }
}

function resultsQuery(config: BrowseSearchConfig, state: BrowseState, plan: SearchPlan, { indexName, algoliaFilters }: QueryContext): MultipleQueriesQuery {
  return {
    indexName,
    params: {
      query: searchedKeyword(config, state.keyword),
      facetFilters: facetFilters(config, state),
      filters: algoliaFilters,
      facets: attributesToCount(config, plan.hitsFacetKeys),
      ...firstHits(hitsPerPageFor(config, state)),
      clickAnalytics: true
    }
  }
}

function countQuery(config: BrowseSearchConfig, state: BrowseState, countPlan: CountQueryPlan, { indexName, algoliaFilters }: QueryContext): MultipleQueriesQuery {
  const stateToCountUnder = withCleared(state, countPlan.excludedKeys)
  return {
    indexName,
    params: {
      query: searchedKeyword(config, stateToCountUnder.keyword),
      facetFilters: facetFilters(config, stateToCountUnder),
      filters: countPlan.appliesAlgoliaFilters ? algoliaFilters : '',
      facets: attributesToCount(config, countPlan.facetKeys),
      ...firstHits(0),
      analytics: false
    }
  }
}

// The results query comes first, then one count query per entry in the plan.
export function buildQueries(config: BrowseSearchConfig, state: BrowseState, plan: SearchPlan = config.searchPlan(state)): MultipleQueriesQuery[] {
  const context: QueryContext = { indexName: config.indexName(state), algoliaFilters: config.algoliaFilters?.(state) ?? '' }
  const countQueries = plan.countQueries.map((countPlan) => countQuery(config, state, countPlan, context))
  return [resultsQuery(config, state, plan, context), ...countQueries]
}

type FacetsByAttribute = Record<string, Record<string, number>> | undefined

// A facet that was asked for but came back absent had no matching records, so it is an empty
// count, which is different from a facet nobody asked for (undefined).
function countsForKeys(config: BrowseSearchConfig, keys: string[], facetsByAttribute: FacetsByAttribute): FacetCounts {
  const countsByKey = keys.map((key) => [key, facetsByAttribute?.[attributeOf(config, key)] ?? {}])
  return Object.fromEntries(countsByKey)
}

// Responses arrive in the order buildQueries sent the queries: results first, then each count query.
export function facetCountsFromResults(config: BrowseSearchConfig, plan: SearchPlan, results: { facets?: FacetsByAttribute }[]): FacetCounts {
  const [resultsResponse, ...countResponses] = results
  const resultsQueryCounts = countsForKeys(config, plan.hitsFacetKeys, resultsResponse?.facets)
  const countQueryCounts = plan.countQueries.map((countPlan, index) => countsForKeys(config, countPlan.facetKeys, countResponses[index]?.facets))
  return Object.assign({}, resultsQueryCounts, ...countQueryCounts)
}
